import React, { Component } from "react";

// ตัวกรองช่วงวันที่แบบ "วันที่เริ่มต้น" + "วันที่สิ้นสุด" แยก 2 ช่อง ใช้ปฏิทินป๊อปอัปอันเดียว
// กดช่องไหนก็เปิดปฏิทินเดียวกัน: คลิกแรก = เริ่มต้น, คลิกที่สอง = สิ้นสุด (ถ้าน้อยกว่าเริ่มต้นให้สลับ)
// startValue/endValue/min/max เป็น "YYYY-MM-DD" (ค่าว่าง = ""), onChange({ start, end }) ส่งกลับรูปแบบเดียวกัน
// render เป็น Fragment ของ 2 ช่อง (label + input) เพื่อให้เข้ากับ grid/flex ของหน้าเดิมโดยไม่ต้องเปลี่ยน layout
const TH_MONTHS = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const EN_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TH_DAYS = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
const EN_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const CAL_WIDTH = 300;
const CAL_HEIGHT = 330;

const pad = (n) => ("0" + n).slice(-2);
const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// "YYYY-MM-DD" -> Date เที่ยงคืนเวลาท้องถิ่น (new Date("YYYY-MM-DD") จะได้เที่ยงคืน UTC แล้ววันเลื่อน)
const fromKey = (v) => {
  const m = typeof v === "string" && v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const toDisplay = (v) => {
  const d = fromKey(v);
  return d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : "";
};
// พิมพ์ dd/mm/yyyy หรือ ddmmyyyy (ปี พ.ศ. > 2100 แปลงเป็น ค.ศ.) คืน "YYYY-MM-DD" หรือ null ถ้าไม่ถูกต้อง
const parseTyped = (text) => {
  const t = (text || "").trim();
  let m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m && /^\d{8}$/.test(t)) m = [t, t.slice(0, 2), t.slice(2, 4), t.slice(4)];
  if (!m) return null;
  let year = Number(m[3]);
  if (year > 2100) year -= 543;
  const d = new Date(year, Number(m[2]) - 1, Number(m[1]));
  if (d.getFullYear() !== year || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[1])) return null;
  return toKey(d);
};

const CalendarIcon = () => (
  <svg style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#d2d2d2" viewBox="0 0 448 512">
    <path d="M152 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L64 64C28.7 64 0 92.7 0 128l0 16 0 48L0 448c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-256 0-48 0-16c0-35.3-28.7-64-64-64l-40 0 0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40L152 64l0-40zM48 192l80 0 0 56-80 0 0-56zm0 104l80 0 0 64-80 0 0-64zm128 0l96 0 0 64-96 0 0-64zm144 0l80 0 0 64-80 0 0-64zm80-48l-80 0 0-56 80 0 0 56zm0 160l0 40c0 8.8-7.2 16-16 16l-64 0 0-56 80 0zm-128 0l0 56-96 0 0-56 96 0zm-144 0l0 56-64 0c-8.8 0-16-7.2-16-16l0-40 80 0zM272 248l-96 0 0-56 96 0 0 56z" />
  </svg>
);

class DateRangeFields extends Component {
  startRef = React.createRef();
  endRef = React.createRef();
  state = {
    open: false,
    picking: "start", // ช่องที่กำลังรับค่าจากการคลิกปฏิทิน
    pos: null,
    view: new Date(),
    hover: "",
    draft: { start: null, end: null }, // ข้อความที่กำลังพิมพ์ (null = ใช้ค่าจาก props)
  };

  getValue = () => ({ start: this.props.startValue || "", end: this.props.endValue || "" });

  emit = (start, end) => {
    if (this.props.onChange) this.props.onChange({ start: start || "", end: end || "" });
  };

  inBounds = (key) => {
    const { min, max } = this.props;
    return !((min && key < min) || (max && key > max));
  };

  openFor = (field) => {
    const ref = field === "start" ? this.startRef : this.endRef;
    const rect = ref.current ? ref.current.getBoundingClientRect() : null;
    let left = rect ? rect.left : 0;
    let top = rect ? rect.bottom + 4 : 0;
    if (rect) {
      if (left + CAL_WIDTH > window.innerWidth) left = window.innerWidth - CAL_WIDTH - 8;
      if (left < 8) left = 8;
      if (rect.bottom + CAL_HEIGHT > window.innerHeight) top = rect.top - CAL_HEIGHT - 4;
      if (top < 8) top = 8;
    }
    const value = this.getValue();
    const focusDate = fromKey(field === "end" ? value.end || value.start : value.start || value.end) || new Date();
    this.setState({
      open: true,
      picking: field,
      pos: { left, top },
      view: new Date(focusDate.getFullYear(), focusDate.getMonth(), 1),
    });
  };

  close = () => this.setState({ open: false, hover: "" });

  pickDay = (key) => {
    if (!this.inBounds(key)) return;
    const { start, end } = this.getValue();
    if (this.state.picking === "start") {
      // คลิกแรก = เริ่มต้น (เคลียร์สิ้นสุดถ้าอยู่ก่อนเริ่มต้นใหม่) แล้วรอคลิกสิ้นสุด
      this.emit(key, end && end >= key ? end : "");
      this.setState({ picking: "end", draft: { start: null, end: null } });
    } else {
      let s = start;
      let e = key;
      if (!s) {
        // ยังไม่มีเริ่มต้น: ถือว่าวันที่คลิกเป็นเริ่มต้น แล้วรอสิ้นสุดต่อ
        this.emit(key, "");
        this.setState({ picking: "end", draft: { start: null, end: null } });
        return;
      }
      if (e < s) [s, e] = [e, s];
      this.emit(s, e);
      this.setState({ open: false, hover: "", draft: { start: null, end: null } });
    }
  };

  commitTyped = (field) => {
    const text = this.state.draft[field];
    if (text === null) return;
    const value = this.getValue();
    const key = parseTyped(text);
    this.setState({ draft: { ...this.state.draft, [field]: null } });
    if (text.trim() === "") {
      this.emit(field === "start" ? "" : value.start, field === "end" ? "" : value.end);
      return;
    }
    if (!key || !this.inBounds(key)) return; // พิมพ์ผิดรูปแบบ/นอกช่วง = คืนค่าเดิม
    let s = field === "start" ? key : value.start;
    let e = field === "end" ? key : value.end;
    if (s && e && e < s) [s, e] = [e, s];
    this.emit(s, e);
  };

  moveView = (delta) => {
    const v = this.state.view;
    this.setState({ view: new Date(v.getFullYear(), v.getMonth() + delta, 1) });
  };

  renderInput = (field, label, placeholder) => {
    const value = this.getValue()[field];
    const draft = this.state.draft[field];
    const active = this.state.open && this.state.picking === field;
    const { className, style, disabled, fieldStyle } = this.props;
    return (
      <div style={fieldStyle}>
        {label ? <label>{label}</label> : null}
        <div style={{ position: "relative" }}>
          <input
            ref={field === "start" ? this.startRef : this.endRef}
            type="text"
            className={className}
            style={{ cursor: "pointer", ...(active ? { borderColor: "#2A55E5", boxShadow: "0 0 0 2px rgba(42,85,229,.15)" } : null), ...style }}
            value={draft !== null ? draft : toDisplay(value)}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={10}
            data-testid={`date-range-${field}`}
            onFocus={() => this.openFor(field)}
            onClick={() => !this.state.open && this.openFor(field)}
            onChange={(e) => this.setState({ draft: { ...this.state.draft, [field]: e.target.value } })}
            onBlur={() => this.commitTyped(field)}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.target.blur();
              if (e.key === "Escape") this.close();
            }}
          />
          <CalendarIcon />
        </div>
      </div>
    );
  };

  renderCalendar = () => {
    const isTh = this.props.type !== "en";
    const { view, hover, picking, pos } = this.state;
    const { start, end } = this.getValue();
    const year = view.getFullYear();
    const month = view.getMonth();
    const cells = [];
    for (let i = 0; i < new Date(year, month, 1).getDay(); i++) cells.push(null);
    for (let d = 1; d <= new Date(year, month + 1, 0).getDate(); d++) cells.push(new Date(year, month, d));
    // ระหว่างรอคลิกสิ้นสุด ใช้วันที่ชี้เมาส์เป็นปลายช่วงชั่วคราว
    const previewEnd = !end && start && picking === "end" && hover ? hover : end;
    const lo = start && previewEnd && previewEnd < start ? previewEnd : start;
    const hi = start && previewEnd && previewEnd < start ? start : previewEnd;
    const years = [];
    for (let y = year - 50; y <= year + 50; y++) years.push(y);
    const arrow = (dir) => (
      <span style={{ cursor: "pointer", padding: "0.6rem 1rem" }} onClick={() => this.moveView(dir)} data-testid={dir < 0 ? "date-range-prev" : "date-range-next"}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="12" height="12">
          <path d={dir < 0
            ? "M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z"
            : "M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"} />
        </svg>
      </span>
    );
    return (
      <>
        <div style={{ position: "fixed", inset: 0, zIndex: 99998, backgroundColor: "#0000" }} onClick={this.close} />
        <div
          data-testid="date-range-calendar"
          style={{
            position: "fixed", left: pos ? pos.left : 0, top: pos ? pos.top : 0, width: CAL_WIDTH, zIndex: 99999,
            padding: "0.3rem 0.5rem", backgroundColor: "#fff", border: "1px solid rgba(0,0,0,.125)", borderRadius: 8,
            boxShadow: "0 0.3rem 1rem rgba(0,0,0,.15)", fontSize: "0.8rem", color: "#000",
          }}
        >
          <div style={{ textAlign: "center", fontSize: 11.5, color: "#6b7280", padding: "2px 0 0" }}>
            {picking === "start" ? (isTh ? "เลือกวันที่เริ่มต้น" : "Select start date") : (isTh ? "เลือกวันที่สิ้นสุด" : "Select end date")}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {arrow(-1)}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <select style={{ borderWidth: 0, textAlign: "center", background: "transparent" }} value={month} onChange={(e) => this.setState({ view: new Date(year, Number(e.target.value), 1) })}>
                {(isTh ? TH_MONTHS : EN_MONTHS).map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
              <select style={{ borderWidth: 0, textAlign: "center", background: "transparent" }} value={year} onChange={(e) => this.setState({ view: new Date(Number(e.target.value), month, 1) })}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {arrow(1)}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {(isTh ? TH_DAYS : EN_DAYS).map((d) => (
              <div key={d} style={{ width: "14.28%", padding: "0.25rem 0", textAlign: "center", fontWeight: 600 }}>{d}</div>
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", minHeight: 198 }}>
            {cells.map((date, idx) => {
              if (!date) return <div key={`e${idx}`} style={{ width: "14.28%", height: 32 }} />;
              const key = toKey(date);
              const ok = this.inBounds(key);
              const isEdge = key === start || key === end;
              const inRange = lo && hi && key >= lo && key <= hi;
              return (
                <div
                  key={key}
                  data-date={key}
                  onClick={() => this.pickDay(key)}
                  onMouseEnter={() => this.setState({ hover: key })}
                  style={{
                    width: "14.28%", height: 32, lineHeight: "32px", textAlign: "center",
                    cursor: ok ? "pointer" : "not-allowed", opacity: ok ? 1 : 0.35,
                    backgroundColor: isEdge ? "#2A55E5" : inRange ? "rgba(42,85,229,0.15)" : "transparent",
                    color: isEdge ? "#fff" : "#000",
                    borderRadius: isEdge ? 8 : 0,
                    fontWeight: key === toKey(new Date()) ? 700 : 400,
                  }}
                >
                  {date.getDate()}
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "0.4rem 0.3rem 0.2rem" }}>
            <label
              style={{ cursor: "pointer", color: "#2A55E5" }}
              onClick={() => { this.emit("", ""); this.setState({ picking: "start", draft: { start: null, end: null } }); }}
            >
              {isTh ? "ล้าง" : "Clear"}
            </label>
            <label style={{ cursor: "pointer", color: "#2A55E5" }} onClick={this.close}>{isTh ? "ปิด" : "Close"}</label>
          </div>
        </div>
      </>
    );
  };

  render() {
    const isTh = this.props.type !== "en";
    const { startLabel, endLabel, startPlaceholder, endPlaceholder } = this.props;
    const ph = isTh ? "วว/ดด/ปปปป" : "dd/mm/yyyy";
    return (
      <>
        {this.renderInput("start", startLabel, startPlaceholder || ph)}
        {this.renderInput("end", endLabel, endPlaceholder || ph)}
        {this.state.open && !this.props.disabled ? this.renderCalendar() : null}
      </>
    );
  }
}

// แปลงระหว่าง Date (state เดิมของบางหน้า) กับ "YYYY-MM-DD" ที่ DateRangeFields ใช้
export const dateToKey = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  return toKey(new Date(value));
};
export const keyToDate = (key) => fromKey(key);

export default DateRangeFields;
